import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import { useTheme } from '../../../../../theme/useTheme';

interface StatusPillProps {
  label: string;
  count: number;
  variant: 'active' | 'pending' | 'removal' | 'disabled';
  onPress?: () => void;
  isDark?: boolean;
}

const PILL_STYLE_LIGHT = {
  active:   { bg: '#D7F0DA', text: '#176B2C' },
  pending:  { bg: '#FFE8B8', text: '#8A5A00' },
  removal:  { bg: '#FFE0D8', text: '#9C2300' },
  disabled: { bg: '#E9EDF2', text: '#5B6470' },
};

const PILL_STYLE_DARK = {
  active:   { bg: 'rgba(56,182,56,0.22)',   text: '#8FEBA8' },
  pending:  { bg: 'rgba(217,150,0,0.22)',   text: '#FFD166' },
  removal:  { bg: 'rgba(220,80,50,0.22)',   text: '#FF9980' },
  disabled: { bg: 'rgba(176,176,176,0.18)', text: '#D4D4D4' },
};

const StatusPill: React.FC<StatusPillProps> = ({ label, count, variant, onPress, isDark }) => {
  const palette = isDark ? PILL_STYLE_DARK[variant] : PILL_STYLE_LIGHT[variant];
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={onPress ? 0.7 : 1}
      style={[styles.pill, { backgroundColor: palette.bg }]}
    >
      <Text style={[styles.pillTxt, { color: palette.text }]}>
        <Text style={styles.pillCount}>{count}</Text>
        {' '}{label}
      </Text>
    </TouchableOpacity>
  );
};

interface SummaryStripProps {
  total: number;
  filtered: number;
  activeCount: number;
  pendingCount: number;
  removalCount: number;
  disabledCount: number;
  loading?: boolean;
  onFilterByStatus?: (status: string) => void;
}

const SummaryStrip: React.FC<SummaryStripProps> = ({
  total,
  filtered,
  activeCount,
  pendingCount,
  removalCount,
  disabledCount,
  loading,
  onFilterByStatus,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <View style={styles.strip}>
      {/* Result count */}
      <Text style={styles.countTxt}>
        {loading
          ? 'Loading…'
          : `Showing ${filtered} of ${total} locations`}
      </Text>

      {/* Status pills */}
      <View style={styles.pills}>
        {!loading && activeCount > 0 && (
          <StatusPill
            label="Active"
            count={activeCount}
            variant="active"
            onPress={() => onFilterByStatus?.('Active')}
            isDark={isDark}
          />
        )}
        {!loading && pendingCount > 0 && (
          <StatusPill
            label="Pending"
            count={pendingCount}
            variant="pending"
            onPress={() => onFilterByStatus?.('Pending Activation')}
            isDark={isDark}
          />
        )}
        {!loading && removalCount > 0 && (
          <StatusPill
            label="Removal"
            count={removalCount}
            variant="removal"
            onPress={() => onFilterByStatus?.('Marked for Removal')}
            isDark={isDark}
          />
        )}
        {!loading && disabledCount > 0 && (
          <StatusPill
            label="Disabled"
            count={disabledCount}
            variant="disabled"
            onPress={() => onFilterByStatus?.('Disabled')}
            isDark={isDark}
          />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  strip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    minHeight: 28,
    marginHorizontal: 0,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  countTxt: {
    fontSize: 13,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
    flexShrink: 0,
  },
  pills: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  pill: {
    height: 28,
    paddingHorizontal: 10,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pillTxt: {
    fontSize: 11,
    fontWeight: '700',
    fontFamily: FontFamily.bold,
  },
  pillCount: {
    fontFamily: FontFamily.bold,
    fontWeight: '800',
  },
});

export default SummaryStrip;
